import Avatar from '@mui/material/Avatar'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Tooltip from '@mui/material/Tooltip'
import { PPSelect, PPSelectOptions } from 'components/PPSelect'
import { ALLOW_PERSIST_TIKTOK_SETTINGS } from 'config'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { firstValueFrom } from 'rxjs'
import { setTikTokProfileSettings } from 'services/compose'
import { saveTikTokProfileSettings } from 'services/compose/tiktok/net'
import { ALLOW_OPTION_KEYS, emptyTikTokSettings, withPostTypeConstraints } from 'services/compose/tiktok/utils'
import { PostDestination, TikTokPostProfileSettings, TikTokPrivacyOptions, TikTokPromotionType } from 'shared'
import { StoreThunkDispatch } from 'store/state'
import styles from './TikTokOptions.pcss'

interface TikTokPostSettingsProps {
  settings: TikTokPostProfileSettings
  options: TikTokPrivacyOptions
  profile: PostDestination
  hideSaveOption?: boolean
  isPhotoPost: boolean
  showPhotoOptions?: boolean
  /** Whether the profile already has a "Remember this choice" record, so the checkbox can start out reflecting it. */
  hasSavedSettings?: boolean
  onChange?: (settings: TikTokPostProfileSettings, persist?: boolean) => void
}

const PRIVACY_OPTIONS_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Public (Everyone)',
  MUTUAL_FOLLOW_FRIENDS: 'Friends (Followers that follow you back)',
  FOLLOWER_OF_CREATOR: 'Followers (People who follow you)',
  SELF_ONLY: 'Private (Visible to me only)'
}

const PROMOTION_OPTIONS_LABELS: Record<string, string> = {
  OFF: 'Off',
  SELF: 'Yes, my brand',
  '3RD-PARTY': 'Yes, 3rd-party brand',
  BOTH: 'Yes, my brand + 3rd party brand'
}

const EMPTY_SELECT_OPTION = { empty: { label: 'Select an option', disabled: true, hidden: true } }

const PROMOTION_OPTIONS: PPSelectOptions = Object.keys(PROMOTION_OPTIONS_LABELS).reduce((acc, option) => {
  acc[option] = { label: PROMOTION_OPTIONS_LABELS[option] }
  return acc
}, { ...EMPTY_SELECT_OPTION } as PPSelectOptions)

const AVATAR_SIZE = { width: 24, height: 24 }

/** Two entries means the profile lets the user choose; a single entry means the value is forced. */
const CHOOSABLE = 2

export const TikTokPostSettings = memo(function TikTokPostSettings({
  settings, options, profile, isPhotoPost, showPhotoOptions, hideSaveOption, hasSavedSettings, onChange
}: TikTokPostSettingsProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  // Usually mounted once the settings have been resolved, so the saved record is already known here.
  const [shouldSaveSettings, setShouldSaveSettings] = useState(Boolean(hasSavedSettings))
  const savedRecordKnown = useRef(hasSavedSettings !== undefined)

  // Callers that render before their fetch resolves report the saved record late. Adopt it once, and only before the
  // user has had anything to toggle - unchecking the box wipes the stored record, so it must never lie about one.
  useEffect(() => {
    if (!savedRecordKnown.current && hasSavedSettings !== undefined) {
      savedRecordKnown.current = true
      setShouldSaveSettings(hasSavedSettings)
    }
  }, [hasSavedSettings])

  // Controlled: the parent owns the settings and feeds every change straight back through `settings`.
  const values = useMemo(() => withPostTypeConstraints(settings, isPhotoPost), [settings, isPhotoPost])

  const notifyChange = useCallback((next: TikTokPostProfileSettings) => {
    if (onChange) {
      onChange(next)
    } else {
      dispatch(setTikTokProfileSettings(next))
    }
  }, [dispatch, onChange])

  // Rendering the stripped values is not enough: the owner still holds - and would submit - the originals. This only
  // fires when `withPostTypeConstraints` actually changed something, so the callback settles after one round trip.
  useEffect(() => {
    if (values !== settings) {
      notifyChange(values)
    }
  }, [values, settings, notifyChange])

  const persistSettings = useCallback(({ isAiContent: _perPost, ...next }: TikTokPostProfileSettings) => {
    // EXPL: the AI flag belongs to the post, so "Remember this choice" does not save it
    firstValueFrom(dispatch(saveTikTokProfileSettings(profile.ppid, next))).catch(console.error)
  }, [dispatch, profile.ppid])

  const toggleSaveSettings = useCallback(() => {
    const nextShouldSave = !shouldSaveSettings
    setShouldSaveSettings(nextShouldSave)
    // There is no endpoint to delete a saved record, so unchecking persists a blank one instead - the
    // read path treats falsy values as "nothing saved" and stops restoring the previous choice.
    persistSettings(nextShouldSave ? values : emptyTikTokSettings(profile.ppid))
  }, [persistSettings, profile.ppid, shouldSaveSettings, values])

  const applyChange = useCallback((changes: Partial<TikTokPostProfileSettings>) => {
    const next = withPostTypeConstraints({ ...values, ...changes }, isPhotoPost)
    if (shouldSaveSettings) {
      persistSettings(next)
    }
    notifyChange(next)
  }, [isPhotoPost, notifyChange, persistSettings, shouldSaveSettings, values])

  const allowOptions = useMemo(() => {
    const base: PPSelectOptions = {
      none: { label: isPhotoPost ? 'No comments' : 'No comments, Duets or Stitch' },
      enableComment: { label: 'Comments', disabled: options.enableComment.length < CHOOSABLE }
    }
    if (isPhotoPost) {
      return base
    }
    return {
      ...base,
      enableDuet: { label: 'Duet', disabled: options.enableDuet.length < CHOOSABLE },
      enableStitch: { label: 'Stitch', disabled: options.enableStitch.length < CHOOSABLE }
    }
  }, [isPhotoPost, options.enableComment.length, options.enableDuet.length, options.enableStitch.length])

  const selectedAllowValues = ALLOW_OPTION_KEYS.filter(key => Boolean(values[key])).join(',')

  const onAllowOptionChange = useCallback((allowValues: string) => {
    const selection = allowValues.split(',').filter(Boolean)
    const withSelection = values.enableComment || values.enableDuet || values.enableStitch
    // "none" clears every interaction, but only when there is something to clear - otherwise it is a no-op
    // selection and the individually picked options below must win.
    const cleared = selection.includes('none') && withSelection

    applyChange({
      enableComment: !cleared && selection.includes('enableComment'),
      enableDuet: !cleared && selection.includes('enableDuet'),
      enableStitch: !cleared && selection.includes('enableStitch')
    })
  }, [applyChange, values.enableComment, values.enableDuet, values.enableStitch])

  const privacyOptions = useMemo(() => options.privacyLevel.reduce((acc, option) => {
    acc[option] = {
      label: PRIVACY_OPTIONS_LABELS[option] || option
    }
    return acc
  }, { ...EMPTY_SELECT_OPTION } as PPSelectOptions), [options.privacyLevel])

  const onPrivacyChange = useCallback((option: string) => {
    applyChange({ privacyLevel: option })
  }, [applyChange])

  const onPromotionTypeChange = useCallback((option: string) => {
    applyChange({ promoting: option as TikTokPromotionType })
  }, [applyChange])

  const toggleAutoAddMusic = useCallback(() => {
    applyChange({ autoAddMusic: !values.autoAddMusic })
  }, [applyChange, values.autoAddMusic])

  const avatar = useMemo(() => (
    <Tooltip title={profile.name}>
      <Avatar alt={profile.name} src={profile.image} sx={AVATAR_SIZE} />
    </Tooltip>
  ), [profile.name, profile.image])

  return (
    <div data-testid="tiktok-post-settings">
      <div className={styles.content}>
        <div className={styles.row}>
          <PPSelect
            options={privacyOptions}
            selectedValue={values.privacyLevel || 'empty'}
            raised
            withCaret
            name="Privacy"
            className={styles.select}
            icon={avatar}
            onSelectionChange={onPrivacyChange}
          />
        </div>
        <div className={styles.row}>
          <PPSelect
            options={allowOptions}
            selectedValue={selectedAllowValues || 'none'}
            raised
            withCaret
            multiSelection
            name="Allow"
            className={styles.select}
            icon={avatar}
            onSelectionChange={onAllowOptionChange}
          />
        </div>
        <div className={styles.row}>
          <PPSelect
            name="Promotion video"
            options={PROMOTION_OPTIONS}
            selectedValue={values.promoting || 'empty'}
            raised
            withCaret
            className={styles.select}
            icon={avatar}
            onSelectionChange={onPromotionTypeChange}
          />
        </div>
        {(isPhotoPost || showPhotoOptions) && (
          <div className={styles.row}>
            <FormControlLabel
              label="Automatically add music"
              labelPlacement="end"
              classes={{ label: styles['cb-label'] }}
              control={(
                <Checkbox
                  checked={Boolean(values.autoAddMusic)}
                  color="primary"
                  size="small"
                  onChange={toggleAutoAddMusic}
                />
              )}
            />
          </div>
        )}
        {ALLOW_PERSIST_TIKTOK_SETTINGS && !hideSaveOption && (
          <div className={styles.row}>
            <FormControlLabel
              label="Remember this choice"
              labelPlacement="end"
              classes={{ label: styles['cb-label'] }}
              control={(
                <Checkbox
                  checked={shouldSaveSettings}
                  color="primary"
                  size="small"
                  onChange={toggleSaveSettings}
                />
              )}
            />
          </div>
        )}
      </div>
    </div>
  )
})
